import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Search, 
  User, 
  Mail, 
  AlertCircle,
  Calendar,
  Key,
  Trash2,
  Copy,
  X,
  Package,
  BookOpen,
  Clock,
  Download
} from 'lucide-react';
import api from '../utils/api';
import { formatHours } from '../utils/timeFormatting';
import TutorBookingSuggestion from '../components/TutorBookingSuggestion';

interface Subject {
  id: string;
  name: string;
  category: string;
}

interface HourPackage {
  id: string;
  subjectId: string;
  remainingHours: number;
  totalHours: number;
  subject: {
    name: string;
    category: string;
  };
}

interface Student {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  createdAt: string;
  lastLessonAt?: string | null;
  nextLessonAt?: string | null;
  studentProfile?: {
    description?: string;
    hasHadConsultation?: boolean;
    consultationEnabled?: boolean;
    hasHadAnyBooking?: boolean;
    subjects: Subject[];
  };
  hourPackages?: HourPackage[];
}

// At registration the student's free-text "A bit about you" answer is stored
// as the last line of a multi-line description blob, prefixed with "About:".
// (See RegisterPage.tsx — the blob is "Year group: …\nLevel: …\nrole\nAbout: …".)
// Pull just that answer back out, preserving any line breaks they typed.
const extractAboutFromDescription = (description?: string): string | null => {
  if (!description) return null;
  const match = description.match(/About:\s*([\s\S]*)$/i);
  const about = match?.[1]?.trim();
  return about ? about : null;
};

const StudentsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBios, setExpandedBios] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'recentLesson' | 'upcomingLesson' | 'joined'>('recentLesson');
  const [activityFilter, setActivityFilter] = useState<'all' | 'active' | 'noLessons' | 'upcoming'>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [showBookingSuggestion, setShowBookingSuggestion] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState<{
    studentId: string;
    studentName: string;
    newPassword: string;
  } | null>(null);
  useEffect(() => {
    if (user?.role === 'TUTOR') {
      fetchStudents();
    }
  }, [user]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const [studentsResponse, packagesResponse] = await Promise.all([
        api.get('/admin/students'),
        api.get('/hour-packages/admin/all')
      ]);
      
      // Group hour packages by student
      const packagesByStudent: { [key: string]: HourPackage[] } = {};
      packagesResponse.data.forEach((pkg: any) => {
        if (!packagesByStudent[pkg.studentId]) {
          packagesByStudent[pkg.studentId] = [];
        }
        if (pkg.status === 'ACTIVE' && pkg.remainingHours > 0) {
          packagesByStudent[pkg.studentId].push(pkg);
        }
      });

      // Merge hour packages with student data
      const studentsWithPackages = studentsResponse.data.map((student: Student) => ({
        ...student,
        hourPackages: packagesByStudent[student.id] || []
      }));

      setStudents(studentsWithPackages);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setError('Failed to load students. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId: string, studentName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to delete "${studentName}"? This action cannot be undone and will remove all their bookings, homework, and profile data.`)) {
      return;
    }

    try {
      await api.delete(`/admin/students/${studentId}`);
      fetchStudents();
      window.alert('Student deleted successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to delete student');
    }
  };

  const handleResetPassword = async (studentId: string, studentName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to reset the password for "${studentName}"? This will generate a new password that you'll need to share with the student.`)) {
      return;
    }

    try {
      const response = await api.post(`/users/${studentId}/reset-password`);
      
      setResetPasswordData({
        studentId,
        studentName,
        newPassword: response.data.newPassword
      });
      setShowPasswordModal(true);
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleSuggestBooking = (student: Student, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedStudent(student);
    setShowBookingSuggestion(true);
  };

  const handleToggleConsultation = async (studentId: string, currentState: boolean, event: React.MouseEvent) => {
    event.stopPropagation();
    
    try {
      await api.put(`/admin/students/${studentId}/consultation`, {
        consultationEnabled: !currentState
      });
      fetchStudents();
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to update consultation status');
    }
  };

  const handleStudentClick = (student: Student) => {
    navigate(`/students/${student.id}`);
  };

  const exportStudentsCSV = () => {
    if (students.length === 0) {
      window.alert('No students to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Name',
      'Email', 
      'Phone Number',
      'Registration Date',
      'Subjects',
      'Has Had Consultation',
      'Consultation Enabled',
      'Has Had Booking',
      'Active Hour Packages',
      'Total Remaining Hours',
      'Description'
    ];

    // Convert student data to CSV rows
    const csvRows = students.map(student => {
      const subjects = student.studentProfile?.subjects?.map(s => `${s.name} (${s.category})`).join('; ') || 'None';
      const activePackages = student.hourPackages?.length || 0;
      const totalRemainingHours = student.hourPackages?.reduce((sum, pkg) => sum + pkg.remainingHours, 0) || 0;
      
      return [
        student.name,
        student.email,
        student.phoneNumber || '',
        new Date(student.createdAt).toLocaleDateString(),
        subjects,
        student.studentProfile?.hasHadConsultation ? 'Yes' : 'No',
        student.studentProfile?.consultationEnabled ? 'Yes' : 'No', 
        student.studentProfile?.hasHadAnyBooking ? 'Yes' : 'No',
        activePackages.toString(),
        totalRemainingHours.toString(),
        student.studentProfile?.description || ''
      ];
    });

    // Create CSV content
    const csvContent = [headers, ...csvRows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `students_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    window.alert('Password copied to clipboard!');
  };

  // Unique subjects across all students, for the subject filter dropdown.
  const subjectOptions = Array.from(
    new Map(
      students
        .flatMap(s => s.studentProfile?.subjects ?? [])
        .map(subj => [subj.id, subj] as const),
    ).values(),
  ).sort((a, b) => a.name.localeCompare(b.name));

  const timeOrNull = (value?: string | null) => (value ? new Date(value).getTime() : null);

  const filteredStudents = students
    .filter(student => {
      // Name / email search (unchanged).
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        student.name.toLowerCase().includes(term) ||
        student.email.toLowerCase().includes(term);
      if (!matchesSearch) return false;

      // Activity status filter.
      if (activityFilter === 'active' && !student.lastLessonAt) return false;
      if (activityFilter === 'noLessons' && student.lastLessonAt) return false;
      if (activityFilter === 'upcoming' && !student.nextLessonAt) return false;

      // Subject filter.
      if (subjectFilter !== 'all') {
        const hasSubject = (student.studentProfile?.subjects ?? []).some(s => s.id === subjectFilter);
        if (!hasSubject) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'joined') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (sortBy === 'upcomingLesson') {
        // Soonest upcoming lesson first; students with no upcoming lesson last.
        const aNext = timeOrNull(a.nextLessonAt);
        const bNext = timeOrNull(b.nextLessonAt);
        if (aNext === null && bNext === null) return 0;
        if (aNext === null) return 1;
        if (bNext === null) return -1;
        return aNext - bNext;
      }
      // recentLesson (default): most recent past lesson first; never-attended last.
      const aLast = timeOrNull(a.lastLessonAt);
      const bLast = timeOrNull(b.lastLessonAt);
      if (aLast === null && bLast === null) return 0;
      if (aLast === null) return 1;
      if (bLast === null) return -1;
      return bLast - aLast;
    });

  const formatSubjectCategory = (category: string) => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      default:
        return category;
    }
  };

  const formatLessonDate = (value?: string | null) => {
    if (!value) return null;
    return new Date(value).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getHourPackageSummary = (packages: HourPackage[]) => {
    if (!packages || packages.length === 0) return null;
    
    return packages.map(pkg => (
      <span key={pkg.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        {formatHours(pkg.remainingHours)}h {pkg.subject.name}
      </span>
    ));
  };

  if (user?.role !== 'TUTOR') {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            <AlertCircle className="inline w-5 h-5 mr-2" />
            Only tutors can access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Students</h1>
            <p className="text-gray-600">Manage your students and their hour packages</p>
          </div>
          <button
            onClick={exportStudentsCSV}
            disabled={students.length === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">
            <AlertCircle className="inline w-5 h-5 mr-2" />
            {error}
          </p>
        </div>
      )}

      {/* Search + sort + filter toolbar */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end gap-4">
        <div className="relative w-full lg:max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search students by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Sort */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="recentLesson">Most recent lesson</option>
              <option value="upcomingLesson">Upcoming lesson soonest</option>
              <option value="joined">Recently joined</option>
            </select>
          </div>

          {/* Activity filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Activity</label>
            <select
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value as typeof activityFilter)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All students</option>
              <option value="active">Active (had a lesson)</option>
              <option value="upcoming">Has upcoming lesson</option>
              <option value="noLessons">No lessons yet</option>
            </select>
          </div>

          {/* Subject filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All subjects</option>
              {subjectOptions.map(subj => (
                <option key={subj.id} value={subj.id}>
                  {subj.name} ({formatSubjectCategory(subj.category)})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <p className="mb-4 text-sm text-gray-500">
        Showing {filteredStudents.length} of {students.length} students
      </p>

      {/* Students Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
              <div className="flex gap-2">
                <div className="h-8 bg-gray-200 rounded w-8"></div>
                <div className="h-8 bg-gray-200 rounded w-8"></div>
                <div className="h-8 bg-gray-200 rounded w-8"></div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredStudents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {searchTerm ? 'No students found matching your search.' : 'No students registered yet.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredStudents.map((student) => (
            <div
              key={student.id}
              onClick={() => handleStudentClick(student)}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-primary-300 transition-all cursor-pointer group"
            >
              {/* Student Name */}
              <div className="mb-3">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                  {student.name}
                </h3>
                <p className="text-sm text-gray-500">{student.email}</p>
                {student.phoneNumber && (
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {student.phoneNumber}
                  </p>
                )}
              </div>

              {/* Subjects */}
              <div className="mb-3">
                <div className="flex flex-wrap gap-1">
                  {student.studentProfile?.subjects && student.studentProfile.subjects.length > 0 ? (
                    student.studentProfile.subjects.slice(0, 3).map((subject, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
                      >
                        {subject.name} ({formatSubjectCategory(subject.category)})
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-500">No subjects selected</span>
                  )}
                  {student.studentProfile?.subjects && student.studentProfile.subjects.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{student.studentProfile.subjects.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Lesson activity */}
              <div className="mb-3 text-xs space-y-0.5">
                <p className="text-gray-500 flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  Last lesson: {student.lastLessonAt ? formatLessonDate(student.lastLessonAt) : 'None yet'}
                </p>
                {student.nextLessonAt && (
                  <p className="text-primary-700 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Next lesson: {formatLessonDate(student.nextLessonAt)}
                  </p>
                )}
              </div>

              {/* About (what they wrote at registration) */}
              {extractAboutFromDescription(student.studentProfile?.description) && (
                <div className="mb-3">
                  <span className="block text-xs font-medium text-gray-700 mb-1">About</span>
                  <p
                    id={`student-bio-${student.id}`}
                    className={`text-sm text-gray-600 whitespace-pre-line break-words${expandedBios.has(student.id) ? '' : ' line-clamp-4'}`}
                  >
                    {extractAboutFromDescription(student.studentProfile?.description)}
                  </p>
                  <button
                    type="button"
                    aria-expanded={expandedBios.has(student.id)}
                    aria-controls={`student-bio-${student.id}`}
                    aria-label={`${expandedBios.has(student.id) ? 'Collapse bio for' : 'Show full bio for'} ${student.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpandedBios((previous) => {
                        const next = new Set(previous);
                        if (next.has(student.id)) {
                          next.delete(student.id);
                        } else {
                          next.add(student.id);
                        }
                        return next;
                      });
                    }}
                    className="mt-1 inline-flex min-h-[32px] min-w-[32px] items-center justify-center rounded px-2 text-sm font-semibold text-primary-600 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    {expandedBios.has(student.id) ? 'Show less' : '...'}
                  </button>
                </div>
              )}

              {/* Hour Packages */}
              {student.hourPackages && student.hourPackages.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-1 mb-1">
                    <Package className="w-3 h-3 text-green-600" />
                    <span className="text-xs font-medium text-gray-700">Hour Packages:</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {getHourPackageSummary(student.hourPackages)}
                  </div>
                </div>
              )}

              {/* Consultation Status */}
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {student.studentProfile?.hasHadConsultation && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      <Clock className="w-3 h-3 mr-1" />
                      Had Consultation
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-600">Consultation:</span>
                  <button
                    onClick={(e) => handleToggleConsultation(
                      student.id, 
                      student.studentProfile?.consultationEnabled ?? true, 
                      e
                    )}
                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                      student.studentProfile?.consultationEnabled !== false 
                        ? 'bg-green-600' 
                        : 'bg-gray-300'
                    }`}
                    title={`${student.studentProfile?.consultationEnabled !== false ? 'Disable' : 'Enable'} consultation`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        student.studentProfile?.consultationEnabled !== false 
                          ? 'translate-x-3.5' 
                          : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={(e) => handleResetPassword(student.id, student.name, e)}
                  className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Reset Password"
                >
                  <Key className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => handleSuggestBooking(student, e)}
                  className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Suggest Booking"
                >
                  <Calendar className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => handleDeleteStudent(student.id, student.name, e)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Student"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Booking Suggestion Modal */}
      {showBookingSuggestion && selectedStudent && (
        <TutorBookingSuggestion
          isOpen={showBookingSuggestion}
          onClose={() => {
            setShowBookingSuggestion(false);
            setSelectedStudent(null);
          }}
          onSuggestionCreated={() => {
            setShowBookingSuggestion(false);
            setSelectedStudent(null);
          }}
          preselectedStudent={selectedStudent}
        />
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && resetPasswordData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Password Reset Successful
              </h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setResetPasswordData(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                New password for <strong>{resetPasswordData.studentName}</strong>:
              </p>
              <div className="bg-gray-100 rounded-lg p-3 flex items-center justify-between">
                <code className="text-sm font-mono">{resetPasswordData.newPassword}</code>
                <button
                  onClick={() => copyToClipboard(resetPasswordData.newPassword)}
                  className="text-primary-600 hover:text-primary-700"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <AlertCircle className="inline w-4 h-4 mr-1" />
                Please share this password securely with the student. They can change it after logging in.
              </p>
            </div>
            
            <button
              onClick={() => {
                setShowPasswordModal(false);
                setResetPasswordData(null);
              }}
              className="w-full btn btn-primary"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsPage;